import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link, NavLink } from 'react-router';
import { api } from '../api.js';
import CompetitionFlag from './CompetitionFlag.js';
import styles from './PoolNav.module.css';

/**
 * The header every screen inside a bolão shares: where you are, how to get
 * back out, and a tab bar that stays put while you move between
 * Predictions, Leaderboard and Rules.
 *
 * Those three used to be a menu you passed *through* — the bolão's own page
 * was a list of three buttons, so seeing the leaderboard meant going back to
 * that page first. They're views of the same thing, so they're tabs instead,
 * and the bolão's URL redirects straight to Predictions (`PoolScreen`).
 *
 * Chart is deliberately not a tab: it's a detail view of the leaderboard,
 * reachable from it, and a fourth tab would push the bar into scrolling on a
 * phone for something most people open rarely.
 *
 * Both queries share their cache keys with `AppHeader` and the screens
 * themselves, so this adds no extra round-trips in practice.
 */
export default function PoolNav({ groupId, poolId }: { groupId: string; poolId: string }) {
  const { t } = useTranslation();

  const { data: pool } = useQuery({
    queryKey: ['pool', groupId, poolId],
    queryFn: () => api.getPool(groupId, poolId),
  });
  const { data: group } = useQuery({
    queryKey: ['group', groupId],
    queryFn: () => api.getGroup(groupId),
  });

  const tab = ({ isActive }: { isActive: boolean }) =>
    `${styles.tab} ${isActive ? styles.tabActive : ''}`;

  return (
    <div className={styles.wrapper}>
      <Link to={`/groups/${groupId}`} className={styles.back}>
        ← {group?.name ?? t('common.back')}
      </Link>

      {pool ? (
        <div className={styles.titleRow}>
          <div className={styles.titleLine}>
            <h1 className={styles.title}>{pool.name}</h1>
            {pool.isOrganizer && pool.sportId === 'football' ? (
              <Link
                to={`/groups/${groupId}/bolaos/${poolId}/settings`}
                className={styles.settingsLink}
                aria-label={t('poolSettings.title')}
              >
                {t('settings.icon')}
              </Link>
            ) : null}
          </div>
          <span className={styles.competition}>
            <CompetitionFlag slug={pool.competitionSlug} />
            {pool.competitionDisplayName}
          </span>
        </div>
      ) : null}

      <nav className={styles.tabs}>
        <NavLink end to={`/groups/${groupId}/bolaos/${poolId}/predictions`} className={tab}>
          {t('poolNav.predictions')}
        </NavLink>
        <NavLink end to={`/groups/${groupId}/bolaos/${poolId}/leaderboard`} className={tab}>
          {t('poolNav.leaderboard')}
        </NavLink>
        <NavLink end to={`/groups/${groupId}/bolaos/${poolId}/rules`} className={tab}>
          {t('poolNav.rules')}
        </NavLink>
      </nav>
    </div>
  );
}
